import { container } from "tsyringe";
import moment from "moment";
import { isTestMarketCountry } from "../../../utils/testMarket";
import i18next from "i18next";
import { findTranslation } from "../../i18n/findTranslation";
import { getUrlLocale } from "../../../utils/getUrlLocale";
import { formatDateLocale } from "../../utils/formatDateLocale";
/**
 * Parses a recommendations value that can be either:
 * - A JSON array of objects with name/code properties (e.g., [{"name":"MIA","code":"MIA"}])
 * - A JSON array of strings (e.g., ["MIA","LAX"])
 * - A comma-separated string of codes (e.g., "MIA,LAX,SFO")
 * - An empty JSON array "[]"
 * - null/undefined
 *
 * Returns an object indicating the type and parsed data.
 */
export function parseRecommendations(recommendations) {
    if (recommendations == null || recommendations === "") {
        return { type: "empty" };
    }
    const raw = typeof recommendations === "string" ? recommendations.trim() : recommendations;
    // Try JSON.parse first
    if (typeof raw === "string" && (raw.startsWith("[") || raw.startsWith("{"))) {
        try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) {
                if (parsed.length === 0) {
                    return { type: "empty" };
                }
                // Check if first element is an object with name or code property
                if (typeof parsed[0] === "object" && parsed[0] !== null && ("name" in parsed[0] || "code" in parsed[0])) {
                    return { type: "objects", locations: parsed };
                }
                // Array of strings — treat as codes
                const codes = parsed
                    .map((item) => String(item).trim())
                    .filter((code) => code.length > 0);
                return codes.length > 0 ? { type: "codes", codes } : { type: "empty" };
            }
        }
        catch (_a) {
            // JSON.parse failed — fall through to comma-separated parsing
        }
    }
    // Fallback: comma-separated codes
    if (typeof raw === "string") {
        const codes = raw
            .split(",")
            .map((code) => code.trim())
            .filter((code) => code.length > 0);
        return codes.length > 0 ? { type: "codes", codes } : { type: "empty" };
    }
    return { type: "empty" };
}
const OnLoadValidations = ({ setLocations, setSelectedPickupLocation, setSelectedReturnLocation, setStartDate, setEndDate, setStartTime, setEndTime, setChecked, startDate, endDate, startTime, endTime, selectedPickupLocation, selectedReturnLocation, setRecommendedLocations, userCountry = "US", }) => {
    var _a, _b;
    setLocations([]);
    setRecommendedLocations([]);
    // Clear stale checkout state on every page load.
    // Covers: user left the site (competitor comparison, closed tab),
    // browser killed the tab (low memory), or full page reload.
    // The unmount cleanup may not have fired in these cases.
    sessionStorage.removeItem("onCheckout");
    sessionStorage.removeItem("checkout_intent_id_paypal");
    if (!window.location.href.includes("collections")) {
        sessionStorage.removeItem("itemIndex");
    }
    const normalizedCountry = (userCountry !== null && userCountry !== void 0 ? userCountry : "US").toString().trim().toUpperCase();
    const isTestMarket = isTestMarketCountry(normalizedCountry);
    const getLocationsByName = container.resolve(isTestMarket ? "iUseCaseGetLocationsByName" : "iUseCaseGetLocationByCode");
    const normalizeStorageValue = (value) => {
        if (value == null)
            return "";
        const stringValue = String(value).trim();
        if (stringValue === "" ||
            stringValue.toLowerCase() === "undefined" ||
            stringValue.toLowerCase() === "null") {
            return "";
        }
        return stringValue;
    };
    const hasValue = (value) => normalizeStorageValue(value) !== "";
    const setSessionValue = (key, value) => {
        const normalized = normalizeStorageValue(value);
        if (normalized) {
            sessionStorage.setItem(key, normalized);
        }
        else {
            sessionStorage.removeItem(key);
        }
    };
    const currentURL = window.location.href;
    const url = new URL(currentURL);
    const urlParams = url.searchParams;
    const isCollectionsPage = currentURL.includes("/collections/");
    const pickupLocationCodeDefault = ((_a = window.holacarrentals) === null || _a === void 0 ? void 0 : _a.landingId) !== undefined
        ? ""
        : normalizeStorageValue(sessionStorage.getItem("pickupLocationCode"));
    sessionStorage.removeItem("name");
    sessionStorage.removeItem("phone");
    sessionStorage.removeItem("email");
    const segments = url.pathname.split("/").filter(Boolean);
    const lastSegment = segments.at(-1);
    const iataMatch = currentURL.includes("/pages") && (lastSegment === null || lastSegment === void 0 ? void 0 : lastSegment.match(/-([A-Za-z]{3})$/));
    const endsWithIATA = segments.length > 1 &&
        lastSegment !== undefined &&
        !/^.+-[A-Za-z]{3}$/.test(lastSegment);
    const fallbackIataCode = currentURL.includes("/pages/") && lastSegment
        ? lastSegment.slice(lastSegment.lastIndexOf("-") + 1).toUpperCase()
        : "";
    let picklocationcode = iataMatch
        ? iataMatch[1].toUpperCase()
        : isCollectionsPage || endsWithIATA
            ? normalizeStorageValue(urlParams.get("pickuplocation"))
            : pickupLocationCodeDefault;
    if (!picklocationcode && /^[A-Z]{3}$/.test(fallbackIataCode)) {
        picklocationcode = fallbackIataCode;
    }
    if (iataMatch) {
        console.log("[OnLoad] IATA from /pages URL:", picklocationcode, "| path:", url.pathname);
    }
    const returnLocationCodeDefault = ((_b = window.holacarrentals) === null || _b === void 0 ? void 0 : _b.landingId) !== undefined
        ? ""
        : normalizeStorageValue(sessionStorage.getItem("returnLocationCode"));
    const droplocationcode = iataMatch
        ? (normalizeStorageValue(urlParams.get("returnlocation")) || picklocationcode)
        : (normalizeStorageValue(urlParams.get("returnlocation")) || returnLocationCodeDefault || picklocationcode);
    // WI #8320 — Pick the translation by URL locale, NOT by `i18next.language`
    // (which can be contaminated to "es-DE" by the country selector). Falls
    // back to `description`/`name` when the URL locale has no entry in
    // `translations[]` (e.g. en-GB has only ES/IT/FR/DE/PT in the API response).
    const translateLocation = (location) => {
        var _a, _b, _c;
        if (isTestMarket) {
            return (_a = location === null || location === void 0 ? void 0 : location.description) !== null && _a !== void 0 ? _a : "";
        }
        let nameTranslation = (_c = (_b = location === null || location === void 0 ? void 0 : location.description) !== null && _b !== void 0 ? _b : location === null || location === void 0 ? void 0 : location.name) !== null && _c !== void 0 ? _c : "";
        if (Array.isArray(location === null || location === void 0 ? void 0 : location.translations)) {
            const match = findTranslation(location.translations, getUrlLocale("lower"));
            if (match && typeof match.name === "string" && match.name.trim() !== "") {
                nameTranslation = match.name;
            }
        }
        return hasValue(nameTranslation)
            ? nameTranslation.charAt(0).toUpperCase() + nameTranslation.slice(1)
            : "";
    };
    const getLocationTuple = (location, overrideName) => {
        const displayName = overrideName !== null && overrideName !== void 0 ? overrideName : translateLocation(location);
        return isTestMarket
            ? [location.description, location.description, location.placeType]
            : [displayName, location.code, location.location_type];
    };
    const persistLocationMetadata = (prefix, location) => {
        const countryKey = prefix === "pick" ? "pickCountry" : "dropCountry";
        if (isTestMarket) {
            const placeIdKey = prefix === "pick" ? "pickPlaceId" : "dropPlaceId";
            const dataSourceKey = prefix === "pick" ? "pickDataSource" : "dropDataSource";
            setSessionValue(placeIdKey, location === null || location === void 0 ? void 0 : location.placeId);
            setSessionValue(dataSourceKey, location === null || location === void 0 ? void 0 : location.dataSource);
        }
        sessionStorage.setItem(countryKey, (location === null || location === void 0 ? void 0 : location.country) || null);
    };
    const applyRecommendedLocations = async (recommendations) => {
        if (!recommendations || recommendations.length === 0) {
            setRecommendedLocations([]);
            return;
        }
        const parsed = parseRecommendations(recommendations);
        if (parsed.type === "objects") {
            setRecommendedLocations(parsed.locations.length > 0 ? parsed.locations : []);
            return;
        }
        if (parsed.type === "codes" && parsed.codes.length > 0) {
            const results = await Promise.all(parsed.codes.map((code) => getLocationsByName.handle(code)));
            setRecommendedLocations(results.filter((loc) => loc != null));
            return;
        }
        setRecommendedLocations([]);
    };
    const setLocationFromStorage = () => {
        const storedPickupName = normalizeStorageValue(sessionStorage.getItem("pickupLocation"));
        const storedPickupCode = normalizeStorageValue(sessionStorage.getItem("pickupLocationCode"));
        const storedPickupTypeCode = normalizeStorageValue(sessionStorage.getItem("pickupLocationTypeCode"));
        const storedReturnNameRaw = normalizeStorageValue(sessionStorage.getItem("returnLocation"));
        const storedReturnTypeCode = normalizeStorageValue(sessionStorage.getItem("returnLocationTypeCode"));
        const resolvedReturnName = storedReturnNameRaw ||
            (returnLocationCodeDefault !== "" && returnLocationCodeDefault === storedPickupCode ? storedPickupName : "");
        if (storedPickupCode !== "") {
            setSelectedPickupLocation([
                storedPickupName,
                storedPickupCode,
                storedPickupTypeCode,
            ]);
        }
        else {
            setSelectedPickupLocation([
                selectedPickupLocation[0],
                selectedPickupLocation[1],
                selectedPickupLocation[2],
            ]);
        }
        if (returnLocationCodeDefault !== "") {
            setSelectedReturnLocation([
                resolvedReturnName,
                returnLocationCodeDefault,
                storedReturnTypeCode || storedPickupTypeCode,
            ]);
            return;
        }
        setSelectedReturnLocation([
            selectedReturnLocation[0],
            selectedReturnLocation[1],
            selectedReturnLocation[2],
        ]);
    };
    const setLocationFromUrl = () => {
        const sanitizeParam = (value) => normalizeStorageValue(value);
        const pickupNameFromUrl = sanitizeParam(urlParams.get("pickupLocName"));
        const pickupCodeFromUrl = sanitizeParam(urlParams.get("pickuplocation"));
        const pickupTypeFromUrl = sanitizeParam(urlParams.get("pickupLocationTypeCode")) || selectedPickupLocation[1];
        const returnNameFromUrl = sanitizeParam(urlParams.get("returnLocName"));
        const returnCodeFromUrl = sanitizeParam(urlParams.get("returnlocation"));
        setSelectedPickupLocation([
            pickupNameFromUrl || selectedPickupLocation[0],
            pickupCodeFromUrl || selectedPickupLocation[1],
            pickupTypeFromUrl,
        ]);
        setSelectedReturnLocation([
            returnNameFromUrl ||
                (returnCodeFromUrl !== "" && returnCodeFromUrl === pickupCodeFromUrl
                    ? pickupNameFromUrl
                    : selectedReturnLocation[0]),
            returnCodeFromUrl || selectedReturnLocation[1],
            sanitizeParam(urlParams.get("returnLocationTypeCode")) || selectedPickupLocation[1],
        ]);
    };
    const syncDateAndTimeState = () => {
        if (urlParams.get("pickUpDate")) {
            setStartDate([
                formatDateLocale(urlParams.get("pickUpDate") || "", i18next.language || "en"),
                urlParams.get("pickUpDate"),
            ]);
            setEndDate([
                formatDateLocale(urlParams.get("returnDate") || "", i18next.language || "en"),
                urlParams.get("returnDate"),
            ]);
            setStartTime([
                moment(urlParams.get("pickUpTime"), "HH:mm").format("hh:mm A"),
                urlParams.get("pickUpTime"),
            ]);
            setEndTime([
                moment(urlParams.get("returnTime"), "HH:mm").format("hh:mm A"),
                urlParams.get("returnTime"),
            ]);
            return;
        }
        setStartTime(sessionStorage.getItem("pickupTime") !== null
            ? [sessionStorage.getItem("pickupTime"), sessionStorage.getItem("pickupTime_")]
            : startTime);
        setEndTime(sessionStorage.getItem("returnTime") !== null
            ? [sessionStorage.getItem("returnTime"), sessionStorage.getItem("returnTime_")]
            : endTime);
        setStartDate(sessionStorage.getItem("startDate") !== null
            ? [sessionStorage.getItem("startDate"), sessionStorage.getItem("startDate_")]
            : startDate);
        setEndDate(sessionStorage.getItem("endDate") !== null
            ? [sessionStorage.getItem("endDate"), sessionStorage.getItem("endDate_")]
            : endDate);
    };
    const toggleSearchBars = (showReturnLocation) => {
        var _a, _b, _c, _d, _e;
        const returnContainer = (_a = document.querySelector(".return-location")) === null || _a === void 0 ? void 0 : _a.parentElement;
        const pickupContainer = (_b = document.querySelector(".pickup-location")) === null || _b === void 0 ? void 0 : _b.parentElement;
        const searchbarParent = (_c = document.querySelector(".searchbar")) === null || _c === void 0 ? void 0 : _c.parentElement;
        const dropdownSelected = document.getElementsByClassName("dropdown__filter-selected")[0];
        returnContainer === null || returnContainer === void 0 ? void 0 : returnContainer.classList.toggle("hidden", !showReturnLocation);
        pickupContainer === null || pickupContainer === void 0 ? void 0 : pickupContainer.classList.toggle("only", !showReturnLocation);
        searchbarParent === null || searchbarParent === void 0 ? void 0 : searchbarParent.classList.toggle("both_searchbar", showReturnLocation);
        (_d = document.querySelector(".right_logo_mobile")) === null || _d === void 0 ? void 0 : _d.classList.toggle("both_trust", showReturnLocation);
        (_e = document
            .querySelector(".banner--content-align-left")) === null || _e === void 0 ? void 0 : _e.classList.toggle("both_background", showReturnLocation);
        dropdownSelected === null || dropdownSelected === void 0 ? void 0 : dropdownSelected.classList.toggle("big_input", !showReturnLocation);
    };
    // Immediately set return location from URL param or sessionStorage so the field is not empty while the async fetch resolves
    const immediateReturnName = normalizeStorageValue(urlParams.get("returnLocName")) ||
        normalizeStorageValue(sessionStorage.getItem("returnLocation"));
    const immediatePickupName = normalizeStorageValue(urlParams.get("pickupLocName")) ||
        normalizeStorageValue(sessionStorage.getItem("pickupLocation"));
    if (immediatePickupName) {
        setSelectedPickupLocation([immediatePickupName, picklocationcode, 1]);
        if (picklocationcode && !hasValue(sessionStorage.getItem("pickupLocationCode"))) {
            sessionStorage.setItem("pickupLocationCode", picklocationcode);
        }
    }
    if (droplocationcode) {
        const returnDisplayName = immediateReturnName || immediatePickupName;
        setSelectedReturnLocation([returnDisplayName, droplocationcode, 1]);
        if (!hasValue(sessionStorage.getItem("returnLocation")) && hasValue(returnDisplayName)) {
            sessionStorage.setItem("returnLocation", returnDisplayName);
        }
        if (!hasValue(sessionStorage.getItem("returnLocationCode"))) {
            sessionStorage.setItem("returnLocationCode", droplocationcode);
        }
    }
    if (hasValue(picklocationcode)) {
        void (async () => {
            var _a, _b;
            const rawPickupData = await getLocationsByName.handle(picklocationcode);
            if (!rawPickupData) {
                setLocations([""]);
                return;
            }
            setLocations([rawPickupData]);
            console.log("DATA ON LOAD:", rawPickupData);
            const pickupData = (_a = rawPickupData[0]) !== null && _a !== void 0 ? _a : rawPickupData;
            const hasPickupDisplayData = hasValue(pickupData === null || pickupData === void 0 ? void 0 : pickupData.description) || hasValue(pickupData === null || pickupData === void 0 ? void 0 : pickupData.name);
            sessionStorage.setItem("originalPickUpCode", pickupData.name);
            if (hasPickupDisplayData) {
                const pickupName = translateLocation(pickupData);
                setSelectedPickupLocation(getLocationTuple(pickupData, pickupName));
                sessionStorage.setItem("pickupLocation", pickupName);
                persistLocationMetadata("pick", pickupData);
                sessionStorage.setItem("dropCountry", pickupData.country || null);
                if (picklocationcode === droplocationcode) {
                    setSelectedReturnLocation(getLocationTuple(pickupData, pickupName));
                    sessionStorage.setItem("returnLocation", pickupName);
                    if (isTestMarket) {
                        setSessionValue("dropPlaceId", pickupData.placeId);
                        setSessionValue("dropDataSource", pickupData.dataSource);
                    }
                }
                await applyRecommendedLocations(pickupData.recomendations);
            }
            else {
                setRecommendedLocations([]);
            }
            const rawReturnData = await getLocationsByName.handle(droplocationcode);
            const returnData = (_b = rawReturnData === null || rawReturnData === void 0 ? void 0 : rawReturnData[0]) !== null && _b !== void 0 ? _b : rawReturnData;
            if (!returnData) {
                setLocations([""]);
                return;
            }
            sessionStorage.setItem("originalReturnCode", returnData.name);
            setLocations((prevLocations) => [...prevLocations, returnData]);
            const returnName = translateLocation(returnData);
            setSelectedReturnLocation(getLocationTuple(returnData, returnName));
            sessionStorage.setItem("returnLocation", returnName);
            persistLocationMetadata("drop", returnData);
        })();
    }
    const dropdownSelected = document.getElementsByClassName("dropdown__filter-selected")[0];
    dropdownSelected === null || dropdownSelected === void 0 ? void 0 : dropdownSelected.classList.add("big_input");
    if (sessionStorage.getItem("pickupLocationCode")) {
        setLocationFromStorage();
    }
    else {
        setLocationFromUrl();
    }
    syncDateAndTimeState();
    const finalPickupName = normalizeStorageValue(sessionStorage.getItem("pickupLocation")) ||
        normalizeStorageValue(urlParams.get("pickupLocName")) ||
        immediatePickupName;
    const finalPickupCode = normalizeStorageValue(sessionStorage.getItem("pickupLocationCode")) ||
        normalizeStorageValue(urlParams.get("pickuplocation")) ||
        picklocationcode;
    const finalPickupType = normalizeStorageValue(sessionStorage.getItem("pickupLocationTypeCode")) ||
        normalizeStorageValue(urlParams.get("pickupLocationTypeCode")) ||
        "1";
    const finalReturnName = normalizeStorageValue(sessionStorage.getItem("returnLocation")) ||
        normalizeStorageValue(urlParams.get("returnLocName")) ||
        immediateReturnName;
    const finalReturnCode = normalizeStorageValue(sessionStorage.getItem("returnLocationCode")) ||
        normalizeStorageValue(urlParams.get("returnlocation")) ||
        droplocationcode;
    const finalReturnType = normalizeStorageValue(sessionStorage.getItem("returnLocationTypeCode")) ||
        normalizeStorageValue(urlParams.get("returnLocationTypeCode")) ||
        finalPickupType;
    if (hasValue(finalPickupCode) &&
        finalPickupCode === finalReturnCode) {
        setSelectedReturnLocation([
            finalReturnName || finalPickupName,
            finalReturnCode,
            finalReturnType,
        ]);
    }
    // On IATA landing pages, decide checkbox from sessionStorage directly
    // instead of relying on droplocationcode (which depends on landingId timing).
    // If session has a DIFFERENT return code → user chose it → checkbox on.
    // If session has the SAME code or none → checkbox off.
    if (iataMatch && !hasValue(normalizeStorageValue(urlParams.get("returnlocation")))) {
        const sessionReturn = normalizeStorageValue(sessionStorage.getItem("returnLocationCode"));
        if (!hasValue(sessionReturn) || sessionReturn === picklocationcode) {
            setChecked(false);
            toggleSearchBars(false);
            if (droplocationcode === picklocationcode) {
                setSelectedReturnLocation([
                    immediatePickupName || immediateReturnName || "",
                    picklocationcode,
                    1,
                ]);
            }
            return;
        }
    }
    const hasDifferentLocations = droplocationcode !== picklocationcode;
    if (hasDifferentLocations) {
        toggleSearchBars(true);
        setChecked(true);
        return;
    }
    if (!urlParams.get("pickuplocation") ||
        urlParams.get("pickuplocation") === urlParams.get("returnlocation")) {
        setChecked(false);
        if (!droplocationcode) {
            setSelectedReturnLocation(["", "", 1]);
        }
        else if (droplocationcode === picklocationcode) {
            setSelectedReturnLocation([
                immediatePickupName || immediateReturnName || "",
                picklocationcode,
                1,
            ]);
        }
    }
    setChecked(false);
    toggleSearchBars(false);
    console.log("[OnLoadValidations] final dropoff state", {
        droplocationcode,
        immediateReturnName,
        finalPickupName,
        finalPickupCode,
        finalReturnName,
        finalReturnCode,
        sessionReturnLocation: sessionStorage.getItem("returnLocation"),
        sessionReturnLocationCode: sessionStorage.getItem("returnLocationCode"),
        urlReturnLocName: urlParams.get("returnLocName"),
        urlReturnLocation: urlParams.get("returnlocation"),
    });
};
export default OnLoadValidations;
